import React, { useState } from 'react';
import type { PortfolioItem } from '../types/interfaces';
import { exportToCSV, exportToJSON, exportToPDF, prepareExportData, ExportFormat } from '../utils/export';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolio: PortfolioItem[];
  currency?: string;
  onExport: (format: ExportFormat) => Promise<boolean>;
}

export default function ExportModal({ isOpen, onClose, portfolio, currency = 'usd', onExport }: ExportModalProps) {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleExport = async (format: ExportFormat) => {
    if (portfolio.length === 0) {
      setError('Your portfolio is empty. Add coins before exporting.');
      return;
    }

    setExporting(true);
    setError(null);

    try {
      // Request payment
      const paymentSuccess = await onExport(format);
      
      if (!paymentSuccess) {
        setError('Export cancelled. Payment is required to export data.');
        setExporting(false);
        return;
      }

      // Prepare and export data
      const exportData = prepareExportData(portfolio, currency);

      switch (format) {
        case 'csv':
          exportToCSV(exportData);
          break;
        case 'json':
          exportToJSON(exportData);
          break;
        case 'pdf':
          exportToPDF(exportData);
          break;
      }

      // Close modal after successful export
      setTimeout(() => {
        onClose();
        setExporting(false);
      }, 500);
    } catch (err) {
      setError('An error occurred during export. Please try again.');
      console.error('Export error:', err);
      setExporting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-md w-[90%] relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center z-10"
        >
          &times;
        </button>

        <div className="text-center mb-6">
          <div className="text-5xl mb-3">📤</div>
          <h2 className="text-2xl mb-2 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
            Export Portfolio
          </h2>
          <p className="text-text-secondary text-sm">
            Export your portfolio data and transaction history
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-danger/20 border-2 border-danger rounded-2xl text-danger text-sm text-center">
            {error}
          </div>
        )}

        <div className="space-y-4 mb-6">
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting || portfolio.length === 0}
            className="w-full px-6 py-4 bg-gradient-to-r from-bg to-bg-secondary border-2 border-border rounded-2xl text-text text-base font-semibold cursor-pointer transition-all duration-300 hover:border-primary hover:scale-105 hover:shadow-bubble active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">📄</span>
              <div className="text-left">
                <div className="font-bold">CSV Export</div>
                <div className="text-xs text-text-secondary">Spreadsheet format</div>
              </div>
            </div>
            <span className="text-xs text-primary font-bold">200 sats</span>
          </button>

          <button
            onClick={() => handleExport('json')}
            disabled={exporting || portfolio.length === 0}
            className="w-full px-6 py-4 bg-gradient-to-r from-bg to-bg-secondary border-2 border-border rounded-2xl text-text text-base font-semibold cursor-pointer transition-all duration-300 hover:border-primary hover:scale-105 hover:shadow-bubble active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">💾</span>
              <div className="text-left">
                <div className="font-bold">JSON Export</div>
                <div className="text-xs text-text-secondary">Full data backup</div>
              </div>
            </div>
            <span className="text-xs text-primary font-bold">200 sats</span>
          </button>

          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting || portfolio.length === 0}
            className="w-full px-6 py-4 bg-gradient-to-r from-bg to-bg-secondary border-2 border-border rounded-2xl text-text text-base font-semibold cursor-pointer transition-all duration-300 hover:border-primary hover:scale-105 hover:shadow-bubble active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div className="text-left">
                <div className="font-bold">PDF Export</div>
                <div className="text-xs text-text-secondary">Printable report</div>
              </div>
            </div>
            <span className="text-xs text-primary font-bold">200 sats</span>
          </button>
        </div>

        <div className="bg-gradient-to-br from-bg to-bg-secondary p-4 rounded-2xl border-2 border-primary/20">
          <p className="text-text-secondary text-xs text-center">
            <span className="font-bold">Note:</span> Each export costs 200 satoshis. 
            Exports include your portfolio holdings and BSV payment records.
          </p>
        </div>

        {exporting && (
          <div className="mt-4 text-center text-text-secondary text-sm">
            Processing export...
          </div>
        )}
      </div>
    </div>
  );
}

